import classNames from 'classnames';
import React, { useEffect, useRef, forwardRef } from 'react';
import { Block, ImageBlockContent } from '../types';
import { Nullable } from 'src/types/nullable.type';

type Props = {
  block: Block;
  selected: boolean;
  forceHoverState?: boolean;
  onDelete: () => void;
};

const S3_BUCKET_URL = 'https://s3uploader-s3uploadbucket-a2p6xxn8vv2i.s3.amazonaws.com';
const CDN_URL = 'https://assets-raw.jny.io';

export const ImageBlock = ({ block, selected, forceHoverState = false, onDelete }: Props) => {
  const [fileUrl, setFileUrl] = React.useState<string | undefined>(undefined);

  const blockContent: ImageBlockContent | undefined = block.content.type === 'image' ? block.content : undefined;
  const dominantColor = blockContent?.dominantColor;

  useEffect(() => {
    const file = blockContent?.file;
    const fileUrl = file ? URL.createObjectURL(file) : undefined;
    if (fileUrl) {
      setFileUrl(fileUrl);
    }
    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
      setFileUrl(undefined);
    };
  }, [blockContent?.file]);

  if (!blockContent) {
    return null;
  }

  const renderImageBackgroundNode = (backgroundImageUrl: string) => {
    return <ImageRenderNode url={backgroundImageUrl} fit={blockContent.objectFit} />;
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      onDelete && onDelete();
    }
  };

  return (
    <div
      className={classNames('group w-full h-full relative transition rounded-2xl bg-transparent')}
      onKeyUp={onKeyUp}
      tabIndex={0}
    >
      <div
        className={classNames('absolute transition-all inset-0 rounded-2xl', {
          'bg-neue-gray-darker': !dominantColor,
        })}
        style={{
          ...(dominantColor && { backgroundColor: dominantColor }),
        }}
      >
        {fileUrl && renderImageBackgroundNode(fileUrl)}
        {blockContent.url && renderImageBackgroundNode(blockContent.url)}
      </div>
      <div
        className={classNames('absolute inset-0 pointer-events-none rounded-2xl transition-colors', {
          'bg-bedrock-black/10': selected || forceHoverState,
          'bg-transparent group-hover:bg-bedrock-black/10': !forceHoverState && !selected,
        })}
      ></div>
    </div>
  );
};

interface ImageRenderNodeProps {
  url: string;
  fit: ImageBlockContent['objectFit'];
  onImageLoad?: (imageElement: Nullable<HTMLImageElement>) => void;
}

const ImageRenderNode = forwardRef<HTMLImageElement, ImageRenderNodeProps>(({ url, fit, onImageLoad }, ref) => {
  const [loaded, setLoaded] = React.useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  const onLoad = () => {
    setLoaded(true);
    onImageLoad && onImageLoad(imageRef.current);
  };

  const getCdnUrl = (url: string) => {
    return url.replace(S3_BUCKET_URL, CDN_URL);
  };

  return (
    <img
      ref={imageRef}
      draggable={false}
      src={getCdnUrl(url)}
      onLoad={onLoad}
      crossOrigin='anonymous'
      className={classNames('absolute transition-all inset-0 w-full h-full rounded-2xl', {
        'object-contain': fit === 'contain',
        'object-cover': fit === 'cover',
        'opacity-0': !loaded,
        'opacity-100': loaded,
      })}
    />
  );
});
